import {
    Alignment, Color, DisplayValue, DOMUtil, ElementPadding, ElementPaddingUtil, getThemeColor, getThemeForKey, HeightWidth, Keys, LeftOrRightAlignment,
    Logger, LogManager, MixinUtil, Navigation, StringUtil
} from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import { BusyMixin } from "../../mixins/BusyMixin";
import { BusyMixinInterface } from "../../mixins/BusyMixinInterface";
import { ButtonPropDefinitions, ButtonProps } from "./ButtonProps";
import { ButtonSize } from "./ButtonSize";
import { ButtonStyles } from "./ButtonStyles";
import { ButtonVariant } from "./ButtonVariant";
import { ClickEvent, DomEvent, Layout, ListItemType, SelectionEvent, SelectionListener } from "../..";
import { Component, ContextMenuClickType } from "../../base/Component";
import { ComponentListenerDefs } from "../../base/ComponentListenerDefs";
import { ComponentPropDefinition, ComponentPropDefinitions } from "../../base/ComponentProps";
import { ComponentTypes } from "../../base/ComponentTypes";
import { ContextMenuItemsProvider } from "../../components/contextmenu/ContextMenuLabelProps";
import { Cursor } from "../../base/Cursor";
import { Image } from "../image/Image";
import { ImageProps } from "../image/ImageProps";
import { KeyHandler } from "../../keyhandling/KeyHandler";
import { ListenerListDef } from "../../base/ListenerListDef";
import { ListProps } from "../list/ListProps";
import { ModifierKeys } from "../../keyhandling/KeyModifiers";
import { DropdownProps, Overlay } from "../../page/Overlay";
import { OverlayProps } from "../../page/OverlayProps";

const _dropdownListenerDef: ListenerListDef = { listName: "_dropdownListeners" };

type DropdownType = ListItemType[] | (() => ListItemType[]);

type ButtonPreBusyState = {
    originalProps: Partial<ButtonProps>;
    originalPadding: ElementPadding | null;
}

enum InnerIds {
    IMAGE = "image",
    IMAGE_DOWN_ARROW = "imageDownArrow"
}

export class Button extends Component implements ButtonProps {
    private static log: Logger;
    private _span: HTMLElement;
    private _cancel: boolean;
    private _caption: string;
    private _dropdownItems: DropdownType
    private _dropdownOverlayProps: Partial<OverlayProps>;
    private _dropdownProps: Partial<DropdownProps>;
    private _overlay: Overlay;
    private _default: boolean;
    private _focusable: boolean;
    private _hotkey: string;
    private _image: Image;
    private _imageAlign: LeftOrRightAlignment;
    private _imageName: ImageName;
    private preBusyState: ButtonPreBusyState;
    private _size: ButtonSize;
    private _variant: ButtonVariant;
    private _wrap: boolean;
    private _captionWrap: boolean;
    private _imageWidth: number;
    private _imageHeight: number;
    private _imageRotation: number;
    private _imageTransition: string;
    private _imageColor: Color;
    private _imageProps: Partial<ImageProps>;
    private _imageStroke: Color;
    private _imageStrokeWidth: number;
    private imageDownArrow: Image;
    private _allProps: Partial<ButtonProps>;
    private _link: string;
    private _cancelKeyHandler: KeyHandler;
    private _defaultKeyHandler: KeyHandler;
    private _hotKeyHandler: KeyHandler;
    private _selected: boolean;
    private _isToggle: boolean;
    private _toggleBackgroundColor: Color;
    private _toggleColor: Color;
    private _requiredLayout: string;
    private _canAccessRequiredLayout: boolean;

    constructor(props?: Partial<ButtonProps>) {
        super("button", props);
        this._allProps = { ...props };
        this._element.setAttribute("type", "button");
        this._span = document.createElement("span");
        this._element.append(this._span);
        this.setClassIncluded(ButtonStyles.base);
        this.setClassIncluded(ButtonStyles.hoverEnabled);
        this.setClassIncluded(ButtonStyles.focusEnabled);
        this.setProps(props);
        if (props == null || props.cursor === undefined)
            this.cursor = Cursor.POINTER;
        if (this.wrap === false)
            this._element.style.whiteSpace = "nowrap"
        this.contextMenuClickType = ContextMenuClickType.LEFT;
        this.addClickListener(event => this.handleClick(event));
    }

    private handleClick(event: ClickEvent) {
        this.ripple(null, { event: event });
        if (this.link != null)
            this.linkClicked();
        if (this.dropdownItems != null)
            this.handleDropdownClick();
        if (this.isToggle)
            this.toggle();
    }

    setProps(props: Partial<ButtonProps>) {
        super.setProps(props);
    }

    get allProps(): Partial<ButtonProps> {
        return this._allProps;
    }

    override syncInnerComponentIds() {
        this.setInnerComponentId(this._image, InnerIds.IMAGE);
        this.setInnerComponentId(this.imageDownArrow, InnerIds.IMAGE_DOWN_ARROW);
    }

    private handleDesignerAutoSize() {
        // if (this._designer != null) {
        //   // removing class to calculate width
        //   if (this._size != null)
        //     for (let key in ButtonSize)
        //       this.element.classList.remove("size-" + key);
        //   const compWidth = this._element.getBoundingClientRect().width;
        //   //If this method is run before DOM is loaded, all buttons set to small
        //   if (compWidth === 0)
        //     return;
        //   for (let key in ButtonSize) {
        //     const btnSizeWidth = parseInt(ButtonStyles["size-" + key].width);
        //     if (btnSizeWidth >= compWidth)

        //     if (compWidth > btnSizeWidth) {
        //       let btnSizeArray = Object.values(btnSizes);
        //       const max = btnSizeArray.reduce(function (prev, current) {
        //         return (parseInt(prev.width) > parseInt(current.width)) ? prev : current
        //       })
        //       if (btnSizeWidth === parseInt(max.width)) {
        //         this.size = key;
        //         break;
        //       }
        //     }
        //     else {
        //       this.size = key;
        //       break;
        //     }
        //   }
        // }
    }

    get caption(): string {
        return this._caption;
    }

    set caption(value: string) {
        const oldValue = this._caption;
        if (oldValue !== value) {
            this._caption = value;
            this._span.innerHTML = value;
            this.syncImagePosition(false);
            this.handleDesignerAutoSize();
            this._matchIdToValue(oldValue, value);
        }
    }

    protected _getDefaultEventProp(): string {
        return "onClick";
    }

    get link(): string {
        return this._link;
    }

    set link(value: string) {
        this._link = value;
    }

    private linkClicked() {
        if (this.link != null) {
            // The right side of the nullish operator seems like it would be redundant, but it lets the link work when
            // the button isn't bound to a field in the DataSource (in that case getRelevantModelRow() would return
            // null).
            const row = this.getRelevantModelRow() ?? this?.dataSource?.activeRow;
            const link = DisplayValue.getFormattedDataString(this.link, row, null, true);
            Navigation.navigateTo(link, { newTab: true });
        }
    }

    public get requiredLayout(): string {
        return this._requiredLayout;
    }

    public set requiredLayout(value: string) {
        if (value == this._requiredLayout)
            return;
        this._requiredLayout = value;
        this.syncRequiredLayout();
    }

    private async syncRequiredLayout() {
        if (this.isDeserializing()) {
            this._canAccessRequiredLayout = this.removedByServer !== true;
        } else if (this._requiredLayout != null && this._designer == null) {
            this._canAccessRequiredLayout = await Layout.canAccessLayout(this._requiredLayout);
        } else {
            this._canAccessRequiredLayout = true;
        }
        this._syncVisible();
    }

    private _triggerClickFromKeyEvent(event: ClickEvent) {
        this.getLog().debug("Clicked from key handler", this);
        this.clicked(event);
    }

    override createKeyHandler(key: string, listener?: (event) => void, modifiers?: ModifierKeys, scope?: HTMLElement): KeyHandler {
        if (listener == null)
            return super.createKeyHandler(key, (event) => this._triggerClickFromKeyEvent(event), modifiers, scope);
        return super.createKeyHandler(key, listener, modifiers, scope);
    }

    get hotkey(): string {
        return this._hotkey;
    }

    set hotkey(value: string) {
        if (value === this._hotkey)
            return;
        this._hotkey = value;
        if (value == null)
            this._hotKeyHandler = null;
        else
            this._hotKeyHandler = this.createKeyHandler(value);
    }

    get cancel(): boolean {
        return this._cancel;
    }

    set cancel(value: boolean) {
        if (value === this._cancel)
            return;
        this._cancel = value;
        if (value !== true)
            this._cancelKeyHandler = null;
        else
            this._cancelKeyHandler = this.createKeyHandler(Keys.ESCAPE);
    }

    get default(): boolean {
        return this._default;
    }

    set default(value: boolean) {
        if (value === this._default)
            return;
        this._default = value;
        if (value !== true)
            this._defaultKeyHandler = null;
        else
            this._defaultKeyHandler = this.createKeyHandler(Keys.ENTER);
    }

    override getKeyHandlers(): KeyHandler[] {
        let result = [];
        if (this.keyHandlers != null)
            result = [...this.keyHandlers];
        if (this._hotKeyHandler != null)
            result.push(this._hotKeyHandler);
        if (this._cancelKeyHandler != null)
            result.push(this._cancelKeyHandler);
        if (this._defaultKeyHandler != null)
            result.push(this._defaultKeyHandler);
        return result;
    }

    get focusable(): boolean {
        return this._focusable;
    }

    set focusable(value: boolean) {
        this._focusable = value;
        if (value === true)
            this._element.tabIndex = null;
        else
            this._element.tabIndex = -1;
    }

    override _applyEnabled(value: boolean): void {
        this.commonApplyEnabled(value);
        this.syncImageArrowDownAppearance();
    }

    get wrap(): boolean {
        return this._wrap;
    }

    set wrap(value: boolean) {
        this._wrap = value;
    }

    get captionWrap(): boolean {
        return this._captionWrap;
    }

    set captionWrap(value: boolean) {
        this._captionWrap = value;
        this._span.style.whiteSpace = value ? "" : "nowrap";
    }

    get variant(): ButtonVariant {
        return this._variant;
    }

    set variant(value: ButtonVariant) {
        this._variant = value;
        if (value === ButtonVariant.round) {
            this._element.classList.add(ButtonStyles.round);
            this._element.classList.remove(ButtonStyles.withImage);
        }
        else if (value === ButtonVariant.text) {
            this._element.classList.remove(ButtonStyles.round);
            this._element.classList.add(ButtonStyles.text);
        }
        else
            this._element.classList.remove(ButtonStyles.round);
    }

    public override get visible(): boolean {
        if (this.requiredLayout != null && this._canAccessRequiredLayout === false) {
            return false;
        }
        return super.visible;
    }

    public override set visible(value: boolean) {
        super.visible = value;
    }

    public override get backgroundColor(): Color {
        return super.backgroundColor;
    }

    public override set backgroundColor(value: Color) {
        super.backgroundColor = value;
        if (StringUtil.isEmptyString(this.backgroundColor)) {
            this.removeClass(ButtonStyles.hoverWithBackgroundColor);
        } else {
            this.setClassIncluded(ButtonStyles.hoverWithBackgroundColor);
        }
    }

    protected applyBusy() {
        try {
            if (this.busy === true) {
                const hasImage = this._image != null;
                this.preBusyState ??= this.createPreBusyState(hasImage);
                this.enabled = false;
                if (hasImage) {
                    // If the image is already present, lock in its size so it can't change the size of the button
                    let imageHeightWidth: HeightWidth;
                    this.doWhileDimensionsComputed(() => {
                        imageHeightWidth = DOMUtil.getElementHeightWidth(this._image._element);
                    });
                    this._imageHeight = imageHeightWidth.height;
                    this._imageWidth = imageHeightWidth.width;
                } else {
                    // Lock in the size of the button
                    let buttonHeightWidth: HeightWidth;
                    this.doWhileDimensionsComputed(() => {
                        buttonHeightWidth = DOMUtil.getElementHeightWidth(this._element);
                    });
                    this.height = buttonHeightWidth.height;
                    this.width = buttonHeightWidth.width;
                    // Reduce the button's padding so that more horizontal space is available to accommodate the spinner
                    this._element.style.padding = "4px";
                    // Set the image's height/width based off the height of the button
                    const buttonBasedHeight = Math.max((buttonHeightWidth.height - 16), 1);
                    // Have to take into account both the direct height/width values, and those in imageProps :(
                    const heightFromPropsValue = Number(this.imageProps?.height);
                    const heightValue = this.imageHeight ?? (isNaN(heightFromPropsValue) ? null : heightFromPropsValue);
                    this._imageHeight = heightValue ?? buttonBasedHeight;
                    const widthFromPropsValue = Number(this.imageProps?.width);
                    const widthValue = this.imageWidth ?? (isNaN(widthFromPropsValue) ? null : widthFromPropsValue)
                    this._imageWidth = widthValue ?? buttonBasedHeight;
                }
                this._imageName = ImageName.spinner;
                this.imageUpdated();
                this._image.rotate = true;
            } else if (this._image != null) {
                this._image.rotate = false;
            }
        } finally {
            if (this.busy !== true && this.preBusyState != null) {
                this.setProps(this.preBusyState?.originalProps);
                ElementPaddingUtil.apply(this._element, this.preBusyState?.originalPadding);
                this.preBusyState = null;
            }
        }
    }

    private createPreBusyState(hasImage: boolean): ButtonPreBusyState {
        // only stash the properties that will be changed by the busy state
        const originalState: Partial<ButtonProps> = {
            enabled: this.enabled,
            imageName: this.imageName,
            imageHeight: this.imageHeight,
            imageWidth: this.imageWidth
        };

        if (!hasImage) {
            originalState.height = this.height;
            originalState.width = this.width;
        }

        return {
            originalProps: originalState,
            originalPadding: !hasImage ? ElementPaddingUtil.capture(this._element) : null
        }
    }

    get dropdownItems(): DropdownType {
        return this._dropdownItems;
    }

    set dropdownItems(value: DropdownType) {
        this._dropdownItems = value;
    }

    get dropdownProps() {
        return this._dropdownProps;
    }

    set dropdownProps(value) {
        this._dropdownProps = value;
    }

    get dropdownOverlayProps(): Partial<OverlayProps> {
        return this._dropdownOverlayProps;
    }

    set dropdownOverlayProps(value: Partial<OverlayProps>) {
        this._dropdownOverlayProps = value;
    }

    get dropdownListProps(): Partial<ListProps> {
        return this._dropdownOverlayProps;
    }

    set dropdownListProps(value: Partial<ListProps>) {
        this._dropdownOverlayProps = value;
    }

    get size(): ButtonSize {
        return this._size;
    }

    set size(value: ButtonSize) {
        // if ((value == null || !(value in btnSizes)) && value.indexOf("auto") === -1)
        //   return;
        // // start with a clean slate. this doesn't seem to throw any errors
        // // removing classes that aren't in list.
        // for (let key in btnSizes) {
        //   this._element.classList.remove(classes[key]);
        // }
        // if (value === "auto-size-up") {
        //   this.handleDesignerAutoSize();
        // }
        // else if (value !== "auto" && value !== "auto-size-up") {
        //   this.element.classList.add(classes[value]);
        // }
        this._size = value;
    }

    get imageName(): ImageName {
        return this._imageName;
    }

    set imageName(value: ImageName) {
        this._imageName = value;
        this.imageUpdated();
    }

    get imageWidth(): number {
        return this._imageWidth;
    }

    set imageWidth(value: number) {
        this._imageWidth = value;
        this.imageUpdated();
    }

    get imageHeight(): number {
        return this._imageHeight;
    }

    set imageHeight(value: number) {
        this._imageHeight = value;
        this.imageUpdated();
    }

    get imageRotation(): number {
        return this._imageRotation;
    }

    set imageRotation(value: number) {
        this._imageRotation = value;
        this.imageUpdated();
    }

    public rotateWithTransition(rotation: number, speed: number) {
        this._imageTransition = Image.resolveTransitionValue(speed);
        this.imageRotation = rotation;
    }

    get imageAlign(): LeftOrRightAlignment {
        return this._imageAlign ?? this.getPropertyDefinitions().imageAlign.defaultValue;
    }

    set imageAlign(value: LeftOrRightAlignment) {
        if (value === this._imageAlign)
            return;
        this._imageAlign = value;
        this.syncImagePosition(true);
    }

    get imageProps(): Partial<ImageProps> {
        return this._imageProps;
    }

    set imageProps(value: Partial<ImageProps>) {
        this._imageProps = value;
        this.imageUpdated();
    }

    get imageColor(): Color {
        return this._imageColor;
    }

    set imageColor(value: Color) {
        this._imageColor = value;
        this.imageUpdated();
    }

    get imageStroke(): Color {
        return this._imageStroke;
    }

    set imageStroke(value: Color) {
        this._imageStroke = value;
        this.imageUpdated();
    }

    get imageStrokeWidth(): number {
        return this._imageStrokeWidth;
    }

    set imageStrokeWidth(value: number) {
        this._imageStrokeWidth = value;
        this.imageUpdated();
    }

    get imageTransition(): string {
        return this._imageTransition;
    }

    set imageTransition(value: string) {
        this._imageTransition = value;
        this.imageUpdated();
    }

    private imageUpdated() {
        if (this._imageName == null && this._imageProps?.name == null) {
            if (this._image != null && this._element.contains(this._image._element))
                this._element.removeChild(this._image._element);
            this._image = null;
        }
        else {
            const props: Partial<ImageProps> = {
                name: this._imageName,
                color: this._imageColor,
                rotation: this._imageRotation,
                transition: this._imageTransition,
                id: this.createInnerComponentId(InnerIds.IMAGE),
                ...this.imageProps,
                width: this._imageWidth !== undefined ? this._imageWidth : this.imageProps?.width,
                height: this._imageHeight !== undefined ? this._imageHeight : this.imageProps?.height,
                stroke: this._imageStroke !== undefined ? this._imageStroke : this.imageProps?.stroke,
                strokeWidth: this._imageStrokeWidth !== undefined ? this._imageStrokeWidth : this.imageProps?.strokeWidth
            };


            if (this._image == null)
                this._image = ComponentTypes.createComponentOfType("image", props);
            else
                this._image.setProps(props);
        }

        this.syncImagePosition(false);
        this.syncImageArrowDownAppearance();
        this.handleDesignerAutoSize();
    }

    private syncImagePosition(alignmentChanged: boolean) {
        if (alignmentChanged && this._image != null && this._element.contains(this._image._element)) {
            this._element.removeChild(this._image._element);
        }

        if (this._image == null) {
            this._element.classList.remove(ButtonStyles.withImage);
            return;
        }

        if (!this._element.classList.contains(ButtonStyles.round)) {
            this._element.classList.add(ButtonStyles.withImage);
        }

        if (!this._element.contains(this._image._element)) {
            if (this.imageAlign === Alignment.LEFT) {
                this._element.insertBefore(this._image._element, this._span);
            } else {
                if (this.imageDownArrow != null && this._element.contains(this.imageDownArrow._element)) {
                    this._element.insertBefore(this._image._element, this.imageDownArrow._element);
                } else {
                    this._element.appendChild(this._image._element);
                }
            }
        }

        if (this.caption != null) {
            if (this.imageAlign === Alignment.LEFT && this._image.marginRight === undefined) {
                this._image.marginRight = 4;
            }
            else if (this.imageAlign === Alignment.RIGHT && this._image.marginLeft === undefined) {
                this._image.marginLeft = 4;
            }
        }
    }

    _elementEventOverride(eventName, handler, event) {
        if (eventName === "onClick" && this.default === true)
            event.preventDefault();
    }

    async handleDropdownClick() {
        let items = this.dropdownItems;
        if (typeof items === "function")
            items = items();
        if (items instanceof Promise)
            items = await items;
        this._overlay = Overlay.showDropdown(this,
            items,
            event => this.fireDropdownSelectionListeners(event),
            { width: null, ...this.dropdownListProps },
            { width: null, ...this.dropdownProps },
            this.dropdownOverlayProps)
            .overlay;
    }

    hideDropdown() {
        if (this._overlay != null) {
            Overlay.hideOverlay(this._overlay);
        }
        this._overlay = null;
    }

    public addDropdownSelectionListener(value: SelectionListener): Component {
        return this.addEventListener(_dropdownListenerDef, value) as Component;
    }

    public removeDropdownSelectionListener(value: SelectionListener): Component {
        return this.removeEventListener(_dropdownListenerDef, value) as Component;
    }

    public fireDropdownSelectionListeners(event: SelectionEvent) {
        this.fireListeners(_dropdownListenerDef, event);
    }

    public get selected(): boolean {
        return this._selected;
    }

    public set selected(value: boolean) {
        this._selected = value;
        if (value) {
            this._element.style.backgroundColor = getThemeColor(this.toggleBackgroundColor);
            this._element.style.color = getThemeColor(this.toggleColor);
        }
        else {
            this._element.style.backgroundColor = getThemeColor(this.backgroundColor || "");
            this._element.style.color = getThemeColor(this.color || "");
        }
    }

    public get isToggle(): boolean {
        return this._isToggle;
    }

    public set isToggle(value: boolean) {
        this._isToggle = value;
    }

    public get toggleBackgroundColor(): Color {
        return this._toggleBackgroundColor || getThemeForKey("button.toggleBackgroundColor");
    }

    public set toggleBackgroundColor(value: Color) {
        this._toggleBackgroundColor = value;
    }

    public get toggleColor(): Color {
        return this._toggleColor;
    }

    public set toggleColor(value: Color) {
        this._toggleColor = value;
    }

    public toggle() {
        this.selected = !this.selected;
    }

    override getPropertyDefinitions(): ComponentPropDefinitions {
        return ButtonPropDefinitions.getDefinitions();
    }

    protected override determinePropertyDefaultValue(prop: ComponentPropDefinition): any {
        if (prop.name === "toggleBackgroundColor")
            return getThemeForKey("button.toggleBackgroundColor");
        if (prop.name === "toggleColor")
            return getThemeForKey("button.toggleColor");
        return super.determinePropertyDefaultValue(prop);
    }

    override get serializationName() {
        return "button";
    }

    override get properName(): string {
        return "Button";
    }

    override domEventFiringListeners(event: DomEvent, eventDef: ListenerListDef): boolean {
        if (eventDef === ComponentListenerDefs.click && this.busy)
            return false;
        return super.domEventFiringListeners(event, eventDef)
    }

    public override get color(): Color {
        return super.color;
    }

    public override set color(value: Color) {
        super.color = value;
        this.syncImageArrowDownAppearance();
    }

    public override get contextMenuItems(): ContextMenuItemsProvider {
        return super.contextMenuItems;
    }

    public override set contextMenuItems(value: ContextMenuItemsProvider) {
        super.contextMenuItems = value;
        this.syncImageArrowDown();
    }

    private syncImageArrowDown() {
        if (this.contextMenuItems == null) {
            if (this.imageDownArrow != null && this._element.contains(this.imageDownArrow._element)) {
                this._element.removeChild(this.imageDownArrow._element);
            }
            this.imageDownArrow = null;
        } else {
            if (this.imageDownArrow == null) {
                this.imageDownArrow = new Image({
                    height: 12,
                    id: this.createInnerComponentId(InnerIds.IMAGE_DOWN_ARROW),
                    name: ImageName.caret,
                    width: 12
                });
                this.syncImageArrowDownAppearance();
            }
            if (!this._element.contains(this.imageDownArrow._element)) {
                this._element.appendChild(this.imageDownArrow._element);
            }
        }
    }

    private syncImageArrowDownAppearance() {
        if (this.imageDownArrow != null) {
            if (this.enabled) {
                this.imageDownArrow.color = this.imageColor ?? this._image?.color ?? this.color;
            } else {
                this.imageDownArrow._element.style.color = getThemeColor("subtle.light");
            }
        }
    }

    protected getLog() {
        if (Button.log == null) {
            Button.log = LogManager.getLogger("components.button.Button");
        }
        return Button.log;
    }
}

// Apply mixins at runtime
MixinUtil.applyMixins(Button, [ BusyMixin ]);

// TypeScript interface declaration for mixin integration
export interface Button extends BusyMixinInterface {
}

ComponentTypes.registerComponentType("button", {
    constructor: Button.prototype.constructor
});
